import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Phone, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useLeadModal } from "./LeadModalContext";
import { SITE, waLink, PROBLEM_OPTIONS, EXPERIENCE_OPTIONS } from "../data/site";
import { submitLead, track } from "../lib/track";
import { PrimaryButton } from "./primitives";

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-dwc-charcoal placeholder:text-dwc-charcoal/40 transition-[border-color,box-shadow] duration-200 focus:border-dwc-green focus:outline-none focus:ring-2 focus:ring-dwc-green/20";

const Field = ({ label, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-dwc-charcoal/70">
      {label}
    </label>
    {children}
  </div>
);

export const LeadForm = ({ type = "assessment", inline = false, onDone }) => {
  const [form, setForm] = useState({ name: "", mobile: "", location: "", experience: "", problem: "", preferred_time: "", contact_method: "WhatsApp", appointment_type: "Home Appointment", address: "", car_model: "", transmission: "", own_car: "", dob: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const isFranchise = type === "franchise";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[+]?[0-9\s-]{8,16}$/.test(form.mobile.trim())) {
      setError("Please enter a valid mobile number.");
      return;
    }
    setStatus("submitting");
    try {
      await submitLead({ ...form, type });
      track("assessment_booking", { type });
      setStatus("success");
      onDone && onDone();
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong. Please try WhatsApp or call us directly.");
    }
  };

  if (status === "success") {
    const waMsg = isFranchise
      ? `Hello DWC! I'm ${form.name} from ${form.location || "India"}. I just requested a franchise discovery call on the website.`
      : `Hello DWC! I'm ${form.name} from ${form.location || "my city"}. I just booked an appointment on the website. My main challenge: ${form.problem || "driving confidence"}.`;
    return (
      <div className="py-6 text-center" data-testid="lead-form-success">
        <CheckCircle2 className="mx-auto h-14 w-14 text-dwc-green" aria-hidden="true" />
        <h3 className="mt-4 text-2xl font-extrabold tracking-tight text-dwc-charcoal">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="mt-2 text-sm text-dwc-charcoal/70">{isFranchise ? "Our DWC franchise team will contact you shortly to schedule your discovery call." : "Appointment request received. Our DWC team will contact you shortly to confirm your appointment."}</p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <PrimaryButton href={waLink(waMsg)} testId="lead-success-whatsapp-btn" onClick={() => track("whatsapp_click", { source: "lead_success" })}>
            <MessageCircle className="h-4 w-4" aria-hidden="true" /> Continue on WhatsApp
          </PrimaryButton>
          <a
            href={SITE.phoneHref}
            data-testid="lead-success-call-btn"
            onClick={() => track("phone_click", { source: "lead_success" })}
            className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dwc-green bg-white px-7 py-4 text-sm font-bold uppercase tracking-wide text-dwc-green transition-colors duration-300 hover:bg-dwc-soft"
          >
            <Phone className="h-4 w-4" aria-hidden="true" /> <span className="whitespace-pre">{SITE.phoneDisplay}</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="lead-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" htmlFor="lead-name">
          <input id="lead-name" data-testid="lead-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
        </Field>
        <Field label="Mobile Number" htmlFor="lead-mobile">
          <input id="lead-mobile" data-testid="lead-mobile-input" required value={form.mobile} onChange={set("mobile")} placeholder="+91 ..." inputMode="tel" className={inputCls} />
        </Field>
        <Field label={form.dob ? `Date of Birth (Age: ${Math.max(0, new Date(Date.now() - new Date(form.dob).getTime()).getUTCFullYear() - 1970)} years)` : "Date of Birth"} htmlFor="lead-dob">
          <input id="lead-dob" type="date" data-testid="lead-dob-input" value={form.dob} onChange={set("dob")} max={new Date().toISOString().slice(0, 10)} className={inputCls} />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Location" htmlFor="lead-location">
          <input id="lead-location" data-testid="lead-location-input" value={form.location} onChange={set("location")} placeholder={isFranchise ? "Your city" : "Area / City"} className={inputCls} />
        </Field>
        {!isFranchise && (
          <Field label="Driving Experience" htmlFor="lead-experience">
            <select id="lead-experience" data-testid="lead-experience-select" value={form.experience} onChange={set("experience")} className={inputCls}>
              <option value="">Select experience</option>
              {EXPERIENCE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {!isFranchise && (
          <Field label="Main Driving Challenge" htmlFor="lead-problem">
            <select id="lead-problem" data-testid="lead-problem-select" value={form.problem} onChange={set("problem")} className={inputCls}>
              <option value="">Select your main challenge</option>
              {PROBLEM_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
        )}
        <Field label="Preferred Date/Time" htmlFor="lead-time">
          <input id="lead-time" data-testid="lead-time-input" value={form.preferred_time} onChange={set("preferred_time")} placeholder="e.g. Tomorrow, 5 PM" className={inputCls} />
        </Field>
      </div>
      <Field label="Preferred Contact Method" htmlFor="lead-contact">
        <select id="lead-contact" data-testid="lead-contact-select" value={form.contact_method} onChange={set("contact_method")} className={inputCls}>
          <option value="WhatsApp">WhatsApp</option>
          <option value="Call">Phone Call</option>
        </select>
      </Field>
      {!isFranchise && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Appointment Type" htmlFor="lead-appt-type">
              <select id="lead-appt-type" data-testid="lead-appointment-type-select" value={form.appointment_type} onChange={set("appointment_type")} className={inputCls}>
                <option value="Home Appointment">Home Appointment</option>
                <option value="Online Appointment">Online Appointment</option>
              </select>
            </Field>
            <Field label="Car Model" htmlFor="lead-car-model">
              <input id="lead-car-model" data-testid="lead-car-model-input" value={form.car_model} onChange={set("car_model")} placeholder="e.g. Swift, Nexon EV" className={inputCls} />
            </Field>
          </div>
          {form.appointment_type === "Home Appointment" && (
            <Field label="Address for Home Appointment" htmlFor="lead-address">
              <input id="lead-address" data-testid="lead-address-input" value={form.address} onChange={set("address")} placeholder="Your full address" className={inputCls} />
            </Field>
          )}
          <Field label="Car Type" htmlFor="lead-transmission">
            <select id="lead-transmission" data-testid="lead-transmission-select" value={form.transmission} onChange={set("transmission")} className={inputCls}>
              <option value="">Select car type</option>
              <option value="Manual">Manual</option>
              <option value="Automatic">Automatic</option>
              <option value="EV">EV (Electric)</option>
            </select>
          </Field>
          <div className="rounded-xl border border-dwc-green/20 bg-dwc-soft p-4" data-testid="own-car-block">
            <p className="text-[12px] font-extrabold uppercase tracking-wide text-dwc-green">Important: Training is provided in the learner's own car.</p>
            <p className="mt-2 text-[13px] font-bold text-dwc-charcoal">Do you have your own car available for training? *</p>
            <div className="mt-2 flex gap-3">
              {["Yes", "No"].map((v) => (
                <label key={v} className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border-2 py-2.5 text-sm font-extrabold transition-colors ${form.own_car === v ? "border-dwc-green bg-white text-dwc-green" : "border-gray-200 bg-white text-dwc-charcoal/60"}`}>
                  <input type="radio" name="own_car" value={v} checked={form.own_car === v} onChange={set("own_car")} required data-testid={`own-car-${v.toLowerCase()}`} className="accent-dwc-green" />
                  {v}
                </label>
              ))}
            </div>
            {form.own_car === "No" && (
              <p className="mt-3 rounded-lg bg-dwc-crimson/[0.06] px-3 py-2.5 text-[12px] font-semibold leading-relaxed text-dwc-crimson" data-testid="own-car-no-note">
                Please note: the standard DWC training model requires the learner's own suitable vehicle (Manual, Automatic or EV). Our advisor will discuss your options during the appointment.
              </p>
            )}
          </div>
        </>
      )}
      {error && <p className="text-sm font-semibold text-dwc-crimson" data-testid="lead-form-error">{error}</p>}
      <PrimaryButton type="submit" testId="lead-form-submit-button" className="w-full" onClick={() => {}}>
        {status === "submitting" ? (
          <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Booking...</span>
        ) : (
          isFranchise ? "Book a Discovery Call" : "Book an Appointment"
        )}
      </PrimaryButton>
      <p className="text-center text-xs text-dwc-charcoal/50">No spam. The DWC team will contact you {isFranchise ? "about the franchise opportunity" : "to understand your situation"}.</p>
    </form>
  );
};

export const LeadModal = () => {
  const { isOpen, close, title, type } = useLeadModal();
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-dwc-charcoal/60 backdrop-blur-sm sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Book an appointment"
          data-testid="lead-modal"
        >
          <motion.div
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-dwc-crimson">{SITE.tagline}</p>
                <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-dwc-charcoal">
                  {title || "Book an Appointment"}
                </h2>
              </div>
              <button
                onClick={close}
                data-testid="lead-modal-close-btn"
                aria-label="Close"
                className="rounded-full border border-gray-200 p-2 text-dwc-charcoal/60 transition-colors hover:bg-dwc-soft hover:text-dwc-charcoal"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <LeadForm type={type} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
